import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { request } from "../utils/api";

// Home Health Agency HR Portal styles
const hhaStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob {
    animation: blob 7s infinite alternate;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in {
    animation: fadeIn 0.4s ease-out forwards;
  }
`;

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
           const response = await request("/forget-password", {
                method: "POST",
                body: JSON.stringify({ email: email.trim().toLowerCase() }),
                skipAuth: true,
            });
            setIsSuccess(true);
            if(response.status === 1){
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error("Forgot password error:", error);
            toast.error(
                error.message || "Failed to send reset link. Please try again.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <style>{hhaStyles}</style>
            <div className="min-h-screen flex bg-white font-sans text-gray-900 overflow-hidden">
                {/* Left Side - Full-bleed illustration (hidden on mobile & tablet) */}
                <div className="hidden lg:flex lg:w-1/2 relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-50 via-sky-50/90 to-indigo-100">
                    <img
                        src="/kornivusinfotech.png"
                        alt="HR Illustration"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        onError={(e) => {
                            e.target.style.display = "none";
                        }}
                    />
                    {/* Subtle gradient overlay for depth */}
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/5 via-transparent to-transparent pointer-events-none" aria-hidden />
                </div>

                {/* Right Side - Forgot Password Form */}
                <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 sm:p-12 xl:p-24 bg-white relative">
                    {/* Subtle Mobile Background (if no left panel) */}
                    <div
                        className="absolute inset-0 lg:hidden pointer-events-none opacity-[0.02]"
                        style={{
                            backgroundImage: "radial-gradient(#000 1px, transparent 1px)",
                            backgroundSize: "30px 30px",
                        }}
                    ></div>

                    <div className="w-full max-w-[28rem] mx-auto relative z-10">
                        {/* Logos */}
                        <div className="flex items-center gap-4 mb-10">
                            <img
                                src="/login-logo.png"
                                alt="Logo"
                                className="h-12 w-auto object-contain drop-shadow-sm transition-transform hover:scale-105"
                                onError={(e) => {
                                    e.target.style.display = "none";
                                }}
                            />
                            <img
                                src="/logo.png.png"
                                alt="Logo"
                                className="h-12 w-auto object-contain drop-shadow-sm transition-transform hover:scale-105"
                                onError={(e) => {
                                    e.target.style.display = "none";
                                }}
                            />
                        </div>

                        {/* Header */}
                        <div className="mb-8">
                            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
                                Forgot Password
                            </h2>
                            <p className="text-gray-500 text-[15px]">
                                Enter your email to receive a reset link
                            </p>
                        </div>

                        {isSuccess ? (
                            <div className="text-center animate-fade-in">
                                <div className="bg-green-50/80 border border-green-100 text-green-700 px-5 py-6 rounded-xl mb-6 shadow-sm">
                                    <p className="font-bold text-lg text-green-800">
                                        Check your email
                                    </p>
                                    <p className="text-[15px] mt-2 text-green-700/80 leading-relaxed">
                                        We have sent a password reset link to <br />
                                        <strong className="text-green-900">{email}</strong>.
                                    </p>
                                </div>
                                <Link
                                    to="/login"
                                    className="inline-flex items-center justify-center w-full bg-gray-50 hover:bg-gray-100 text-gray-700 py-3.5 px-6 rounded-xl font-bold text-[15px] border border-gray-200 transition-all duration-200 mt-2"
                                >
                                    Back to Login
                                </Link>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                className="space-y-5 animate-fade-in"
                            >
                                {/* Email Input */}
                                <div className="space-y-1.5 group">
                                    <label
                                        htmlFor="email"
                                        className="block text-sm font-semibold text-gray-700 transition-colors group-focus-within:text-blue-600"
                                    >
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="email"
                                            type="email"
                                            placeholder="name@company.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={(e) => setEmail(String(e.target.value || "").trim().toLowerCase())}
                                            className="block w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500 outline-none text-gray-900 bg-gray-50/50 hover:bg-white transition-all duration-200 placeholder-gray-400"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-6 rounded-xl font-bold text-[15px] shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 mt-8"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg
                                                className="animate-spin h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <span>Send Reset Link</span>
                                    )}
                                </button>

                                <div className="text-center mt-6">
                                    <Link
                                        to="/login"
                                        className="text-[15px] text-gray-500 hover:text-gray-900 font-semibold transition-colors"
                                    >
                                        Wait, I remember my password
                                    </Link>
                                </div>
                            </form>
                        )}

                        {/* Footer */}
                        <div className="mt-12 lg:mt-16 text-center">
                            <p className="text-gray-400 text-sm font-medium">
                                © {new Date().getFullYear()} Kornivus Infotech. All rights
                                reserved.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;